import React from 'react';
import clsx from 'clsx';
import { useFeatures } from '../../context/FeatureContext';
import { money } from '../../utils/format';

// Shared pieces for the Pay Rates engine screens: Settings > Pay Rates, the worker profile's Pay
// Rates card and the timesheet pay lines. One list of the seven rates, one colour per rate (the
// same day colours the pay groups use, so Saturday reads purple everywhere), one date format.

export const RATES = [
  { key: 'weekday',         label: 'Weekday',         unit: 'hr',    hint: 'Monday to Friday, outside the evening window', dot: 'bg-blue-500',    chip: 'bg-blue-50 text-blue-700 ring-blue-200' },
  { key: 'weekday_evening', label: 'Weekday Evening', unit: 'hr',    hint: 'Monday to Friday, inside the evening window',  dot: 'bg-indigo-500',  chip: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  { key: 'saturday',        label: 'Saturday',        unit: 'hr',    hint: 'All hours on Saturday',                          dot: 'bg-purple-500',  chip: 'bg-purple-50 text-purple-700 ring-purple-200' },
  { key: 'sunday',          label: 'Sunday',          unit: 'hr',    hint: 'All hours on Sunday',                            dot: 'bg-orange-500',  chip: 'bg-orange-50 text-orange-700 ring-orange-200' },
  { key: 'public_holiday',  label: 'Public Holiday',  unit: 'hr',    hint: 'All hours on a public holiday',                  dot: 'bg-rose-500',    chip: 'bg-rose-50 text-rose-700 ring-rose-200' },
  { key: 'sleepover',       label: 'Sleepover',       unit: 'shift', hint: 'One fixed amount per sleepover shift, every day', dot: 'bg-violet-500',  chip: 'bg-violet-50 text-violet-700 ring-violet-200' },
  { key: 'km',              label: 'Km',              unit: 'km',    hint: 'Client trips and paid travel between shifts',    dot: 'bg-teal-500',    chip: 'bg-teal-50 text-teal-700 ring-teal-200' },
];
export const RATE_KEYS = RATES.map((r) => r.key);

// Pay line categories: the seven rates plus back pay.
export const CATEGORY = {
  ...Object.fromEntries(RATES.map((r) => [r.key, r])),
  back_pay: { key: 'back_pay', label: 'Back pay', unit: 'hr', dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800 ring-amber-200' },
};
export const categoryLabel = (c) => (CATEGORY[c] && CATEGORY[c].label) || String(c || '').replace(/_/g, ' ');

export const unitText = (unit) => `$/${unit}`;

// '2026-10-29' or an ISO timestamp -> '29/10/2026'. Day strings are read as calendar days, so a
// date can never slip to the day before.
export function dmy(v) {
  if (!v) return 'Not set';
  const s = String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m && s.length <= 10) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export const dayKey = (v) => (v ? String(v).slice(0, 10) : '');

export const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// A rate as money, or a dash when there isn't one.
export const rateText = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? 'Not set' : money(v));

// The seven rates out of any row (a version, a default, a seeded preview), as strings for inputs.
export function ratesForm(src) {
  const o = {};
  for (const k of RATE_KEYS) {
    const v = src && src[k];
    o[k] = v === null || v === undefined || v === '' ? '' : String(Number(v));
  }
  return o;
}

// Inputs back to numbers. Returns { rates } or { error }.
export function parseRates(form) {
  const rates = {};
  for (const r of RATES) {
    const raw = String(form[r.key] ?? '').trim();
    if (raw === '') return { error: `Enter the ${r.label} rate.` };
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return { error: `The ${r.label} rate must be a number of 0 or more.` };
    rates[r.key] = Math.round(n * 10000) / 10000;
  }
  return { rates };
}

export const errText = (e, fallback) => (e && e.response && e.response.data && (e.response.data.message || e.response.data.error)) || fallback;

// Which engine pays this company: 'award' (SCHADS pay groups, the default) or 'rates'.
export function usePayEngine() {
  const features = useFeatures();
  return features && features.pay_engine === 'rates' ? 'rates' : 'award';
}

export function RateLabel({ rate, className }) {
  return (
    <span className={clsx('inline-flex items-center gap-2', className)}>
      <span className={clsx('h-2.5 w-2.5 shrink-0 rounded-full', rate.dot)} aria-hidden="true" />
      <span className="font-medium text-slate-800">{rate.label}</span>
      <span className="text-[12px] text-slate-500">({unitText(rate.unit)})</span>
    </span>
  );
}

export function CategoryChip({ category }) {
  const c = CATEGORY[category] || { label: categoryLabel(category), chip: 'bg-slate-100 text-slate-700 ring-slate-200' };
  return <span className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-medium ring-1 ring-inset whitespace-nowrap', c.chip)}>{c.label}</span>;
}

// Seven money inputs in a grid, each with its colour, unit and (optionally) the value it replaces.
export function RateInputs({ value, onChange, compare, compareLabel = 'Now', idPrefix = 'rate', disabled }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {RATES.map((r) => {
        const id = `${idPrefix}-${r.key}`;
        const before = compare ? compare[r.key] : undefined;
        const changed = compare && value[r.key] !== '' && before !== undefined && before !== null && Number(value[r.key]) !== Number(before);
        return (
          <div key={r.key} className={clsx('rounded-xl border px-3 py-2.5', changed ? 'border-primary-300 bg-primary-50/40' : 'border-slate-200 bg-white')}>
            <label htmlFor={id} className="flex items-center justify-between gap-2 text-[13px]">
              <RateLabel rate={r} />
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                <input id={id} type="number" inputMode="decimal" step="0.01" min="0" disabled={disabled}
                  className="input-field pl-7 tabular-nums" value={value[r.key]}
                  onChange={(e) => onChange({ ...value, [r.key]: e.target.value })} />
              </div>
              <span className="w-12 shrink-0 text-[12px] text-slate-500">per {r.unit}</span>
            </div>
            {compare && (
              <p className="mt-1 text-[11.5px] text-slate-500">{compareLabel}: <span className="tabular-nums">{rateText(before)}</span></p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Times for the evening window, every 15 minutes, with 24:00 written as midnight.
export const TIME_OPTIONS = (() => {
  const out = [];
  for (let m = 0; m <= 24 * 60; m += 15) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    const value = `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    const h12 = h % 12 || 12;
    const ap = h < 12 || h === 24 ? 'am' : 'pm';
    let label = `${h12}:${String(mm).padStart(2, '0')} ${ap}`;
    if (m === 0) label = '12:00 am (start of day)';
    if (m === 24 * 60) label = '12:00 am (midnight)';
    out.push({ value, label });
  }
  return out;
})();
export const hm = (t) => (t ? String(t).slice(0, 5) : '');
export const timeLabel = (t) => {
  const v = hm(t);
  const hit = TIME_OPTIONS.find((o) => o.value === v);
  return hit ? hit.label.replace(' (start of day)', '') : v || 'Not set';
};
